type Options = {
  fieldsUseMinValue?: string[];
  priceRangeSeparator?: string;
  separator: string;
  separatorFilters?: string;
  separatorMultipleSelections?: string;
  splitters?: string[];
};

type ParsedFilter = {
  max?: number;
  min?: number;
  type: string;
  value?: string;
  values?: string[];
  operator?: OperatorType;
};

enum OPERATORS {
  EXCLUSIVE = "exclusive",
  INCLUSIVE = "inclusive",
}

type OperatorType = OPERATORS;

const isExclusiveOperator = (operator: string): operator is OperatorType =>
  operator === OPERATORS.EXCLUSIVE;
const queryFilters = ({
  fieldsUseMinValue = [],
  priceRangeSeparator = "-",
  separator = "|",
  separatorFilters = "+",
  separatorMultipleSelections,
  splitters = [" "],
}: Options) => {
  const getRangeSeparators = (): string[] => {
    const separators = new Set<string>();

    if (priceRangeSeparator) separators.add(priceRangeSeparator);

    try {
      const decoded = decodeURIComponent(priceRangeSeparator);
      if (decoded) separators.add(decoded);
    } catch {
      // Keep original separator when decode fails.
    }

    return Array.from(separators);
  };

  const parseRangeOrMinValue = (key: string, value: string): ParsedFilter[] => {
    const rangeSeparators = getRangeSeparators();

    for (const separatorCandidate of rangeSeparators) {
      const escapedSeparator = separatorCandidate.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );
      const rangeMatch = value.match(
        new RegExp(`^(\\d+)${escapedSeparator}(\\d+)$`)
      );

      if (rangeMatch)
        return [
          {
            max: Number(rangeMatch[2]),
            min: Number(rangeMatch[1]),
            type: key,
          },
        ];
    }

    return [{ min: Number(value), type: key }];
  };

  const parseMultipleSelections = (
    key: string,
    value: string,
    selectionSeparator: string,
    operator?: OPERATORS
  ): ParsedFilter[] => {
    const values = value.split(selectionSeparator);
    return values.map((val) => ({
      type: key,
      value: val,
      ...(operator && { operator }),
    }));
  };

  const splitQueryString = (
    querystring: string,
    querySplitters: string[]
  ): string[] => {
    for (const splitter of querySplitters) {
      if (querystring.includes(splitter)) {
        return querystring.split(splitter);
      }
    }
    return [querystring];
  };

  return {
    options: {
      priceRangeSeparator,
    },
    parse: (querystring: string): ParsedFilter[] => {
      if (!querystring) return [];

      return splitQueryString(querystring, splitters).flatMap((curr) => {
        const [key, value, operatorInput] = curr.split(separator);

        const operator = isExclusiveOperator(operatorInput)
          ? operatorInput
          : undefined;

        if (!key || !value) return [];

        if (fieldsUseMinValue.includes(key))
          return parseRangeOrMinValue(key, value);

        if (
          separatorMultipleSelections &&
          value.includes(separatorMultipleSelections)
        )
          return parseMultipleSelections(
            key,
            value,
            separatorMultipleSelections,
            operator
          );

        return [{ type: key, value, ...(operator && { operator }) }];
      });
    },
    stringify: (dataArray: ParsedFilter[]): string => {
      const resolveValue = (curr: ParsedFilter) => {
        if (separatorMultipleSelections && curr.values?.length)
          return curr.values.join(separatorMultipleSelections);

        if (curr.value !== undefined) return curr.value;

        if (curr.min !== undefined && curr.max !== undefined)
          return `${curr.min}${priceRangeSeparator}${curr.max}`;

        return curr.min ?? "";
      };

      const dataString = dataArray
        .map((curr) => `${curr.type}${separator}${resolveValue(curr)}`)
        .join(separatorFilters);

      return encodeURIComponent(dataString);
    },
  };
};

export default queryFilters;
